import React, { useEffect, useMemo, useState } from 'react';
import './index.css';

import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import MfaSetup from './pages/MfaSetup';
import DashboardAdmin from './pages/DashboardAdmin';
import DashboardTecnico from './pages/DashboardTecnico';
import MapaTecnico from './pages/MapaTecnico';
import FichaCliente from './pages/FichaCliente';
import ResumenVisita from './pages/ResumenVisita';
import RutaTecnico from './pages/RutaTecnico';
import { clearSession, fetchMe, getStoredUser, getToken, setSession } from './services/api';
import { sincronizarVisitasPendientes } from './utils/offlineSync';

const ADMIN_VIEW_META = [
  { id: 'admin', label: 'Panel Admin' },
];

const TECH_VIEW_META = [
  { id: 'tecnico', label: 'Mis Visitas' },
  { id: 'ruta', label: 'Ruta Optimizada' },
  { id: 'mapa', label: 'Mapa' },
  { id: 'ficha', label: 'Ficha Cliente' },
  { id: 'resumen', label: 'Resumen Visita' },
];

const roleValues = (rol) => {
  if (rol === 'admin_tecnico') return ['admin', 'tecnico'];
  return String(rol || '').toLowerCase().split(/[,+|]/).map((item) => item.trim()).filter(Boolean);
};
const hasRole = (rol, role) => (
  (rol === 'superadmin' && ['admin', 'tecnico'].includes(role)) || roleValues(rol).includes(role)
);
const isAdminRole = (rol) => hasRole(rol, 'admin');
const isTecnicoRole = (rol) => hasRole(rol, 'tecnico');
const getDefaultView = (user) => (isAdminRole(user?.rol) ? 'admin' : 'tecnico');
const formatRole = (rol) => ({
  admin: 'Administrador',
  tecnico: 'Tecnico',
  admin_tecnico: 'Administrador y Tecnico',
  superadmin: 'Superadmin',
}[rol] || rol);
const requiresMfaSetup = (user) => Boolean(user) && !user?.totp_enabled;
const THEME_KEY = 'visitas_theme';
const APP_TITLE = 'GasValpo OT Cobranza';
const PRIVILEGED_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const INACTIVITY_WARNING_LEAD_MS = 2 * 60 * 1000;

const getInitialTheme = () => {
  const storedTheme = localStorage.getItem(THEME_KEY);
  if (storedTheme === 'dark' || storedTheme === 'light') {
    return storedTheme;
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

function App() {
  const [usuario, setUsuario] = useState(getStoredUser());
  const [theme, setTheme] = useState(getInitialTheme);
  const [vistaActiva, setVistaActiva] = useState(() => (
    getDefaultView(getStoredUser())
  ));
  const [clienteSeleccionado, setClienteSeleccionado] = useState(() => {
    try {
      const raw = localStorage.getItem('visitas_cliente_seleccionado');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [visitaFinalizada, setVisitaFinalizada] = useState(() => {
    try {
      const raw = localStorage.getItem('visitas_resumen_finalizado');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [validandoSesion, setValidandoSesion] = useState(Boolean(getToken()));
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  useEffect(() => {
    document.title = APP_TITLE;
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [vistaActiva]);

  useEffect(() => {
    const handleSessionExpired = () => {
      localStorage.removeItem('visitas_cliente_seleccionado');
      setUsuario(null);
      setClienteSeleccionado(null);
      setVistaActiva('tecnico');
    };

    window.addEventListener('visitas:session-expired', handleSessionExpired);
    return () => window.removeEventListener('visitas:session-expired', handleSessionExpired);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setValidandoSesion(false);
      return;
    }

    fetchMe()
      .then((user) => {
        setSession(getToken(), user);
        setUsuario(user);
        setVistaActiva(getDefaultView(user));
      })
      .catch(() => {
        clearSession();
        setUsuario(null);
        setVistaActiva('tecnico');
      })
      .finally(() => setValidandoSesion(false));
  }, []);

  useEffect(() => {
    if (!usuario || !isTecnicoRole(usuario.rol)) {
      return;
    }

    const handleOnline = () => {
      void sincronizarVisitasPendientes();
    };

    window.addEventListener('online', handleOnline);
    if (navigator.onLine) {
      void sincronizarVisitasPendientes();
    }

    return () => window.removeEventListener('online', handleOnline);
  }, [usuario]);

  useEffect(() => {
    if (!usuario || !isAdminRole(usuario.rol)) {
      setShowInactivityWarning(false);
      return;
    }

    let warningTimer;
    let logoutTimer;
    const resetInactivityTimers = () => {
      window.clearTimeout(warningTimer);
      window.clearTimeout(logoutTimer);
      setShowInactivityWarning(false);

      warningTimer = window.setTimeout(() => {
        setShowInactivityWarning(true);
      }, PRIVILEGED_INACTIVITY_TIMEOUT_MS - INACTIVITY_WARNING_LEAD_MS);

      logoutTimer = window.setTimeout(() => {
        handleLogout();
        window.alert('Tu sesión se cerró por 15 minutos de inactividad.');
      }, PRIVILEGED_INACTIVITY_TIMEOUT_MS);
    };

    const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimers, { passive: true });
    });
    resetInactivityTimers();

    return () => {
      window.clearTimeout(warningTimer);
      window.clearTimeout(logoutTimer);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimers);
      });
    };
  }, [usuario]);

  const handleSelectCliente = (cliente) => {
    setClienteSeleccionado(cliente);
    localStorage.setItem('visitas_cliente_seleccionado', JSON.stringify(cliente));
    setVistaActiva('ficha');
  };

  const handleVisitaFinalizada = (resumen) => {
    setVisitaFinalizada(resumen);
    localStorage.setItem('visitas_resumen_finalizado', JSON.stringify(resumen));
    localStorage.removeItem('visitas_cliente_seleccionado');
    setClienteSeleccionado(null);
    setVistaActiva('resumen');
  };

  const renderView = (id) => {
    switch (id) {
      case 'admin':
        return <DashboardAdmin />;
      case 'tecnico':
        return <DashboardTecnico onSelectCliente={handleSelectCliente} />;
      case 'ruta':
        return <RutaTecnico onSelectCliente={handleSelectCliente} />;
      case 'mapa':
        return <MapaTecnico onSelectCliente={handleSelectCliente} />;
      case 'ficha':
        return (
          <FichaCliente
            clienteSeleccionado={clienteSeleccionado}
            onVisitaFinalizada={handleVisitaFinalizada}
          />
        );
      case 'resumen':
        return <ResumenVisita visitaFinalizada={visitaFinalizada} />;
      default:
        return <DashboardTecnico onSelectCliente={handleSelectCliente} />;
    }
  };

  const vistas = useMemo(() => {
    if (isAdminRole(usuario?.rol) && isTecnicoRole(usuario?.rol)) {
      return [...ADMIN_VIEW_META, ...TECH_VIEW_META];
    }
    return isAdminRole(usuario?.rol) ? ADMIN_VIEW_META : TECH_VIEW_META;
  }, [usuario]);

  const vista = vistas.find((v) => v.id === vistaActiva) || vistas[0];
  const moduleTitle = vista?.label || 'Módulo';

  const handleLoginSuccess = (user) => {
    setUsuario(user);
    setVistaActiva(getDefaultView(user));
  };

  const handlePasswordChanged = (user) => {
    setUsuario(user);
    setVistaActiva(getDefaultView(user));
  };

  const handleMfaReady = (user) => {
    setUsuario(user);
    setVistaActiva(getDefaultView(user));
  };

  const handleLogout = () => {
    setShowInactivityWarning(false);
    clearSession();
    localStorage.removeItem('visitas_cliente_seleccionado');
    localStorage.removeItem('visitas_resumen_finalizado');
    setUsuario(null);
    setClienteSeleccionado(null);
    setVisitaFinalizada(null);
    setVistaActiva('tecnico');
  };

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'));
  };

  return (
    <div className="gv-shell">
      {showInactivityWarning && (
        <div className="gv-alert gv-alert--warning" style={{ position: 'fixed', right: 16, top: 16, zIndex: 1000, maxWidth: 360 }}>
          Tu sesión se cerrará pronto por inactividad.
        </div>
      )}
      <header className="gv-header">
        <div className="gv-header__brand">
          <img src="/logo-gv-azul-color.png" alt="GasValpo + energias" className="gv-header__logo" />
          <span className="gv-header__title">GasValpo OT Cobranza</span>
        </div>
        <div className="gv-header__actions">
          <button type="button" className="gv-theme-toggle" onClick={toggleTheme}>
            {theme === 'dark' ? 'Tema claro' : 'Tema oscuro'}
          </button>
          {usuario && (
            <>
              <span className="gv-header__badge">{formatRole(usuario.rol)}</span>
              <button type="button" className="gv-btn gv-btn--outline" onClick={handleLogout}>
                Salir
              </button>
            </>
          )}
        </div>
      </header>

      {validandoSesion ? (
        <main className="gv-main gv-fade-in">
          <div className="gv-card" style={{ padding: '28px', textAlign: 'center' }}>
            Verificando sesión...
          </div>
        </main>
      ) : !usuario ? (
        <main className="gv-main gv-fade-in">
          <Login onLoginSuccess={handleLoginSuccess} />
        </main>
      ) : usuario.is_verified === false ? (
        <ChangePassword
          usuario={usuario}
          onPasswordChanged={handlePasswordChanged}
          onLogout={handleLogout}
        />
      ) : requiresMfaSetup(usuario) ? (
        <MfaSetup
          usuario={usuario}
          onMfaReady={handleMfaReady}
          onLogout={handleLogout}
        />
      ) : (
        <>
          <nav className="gv-tabs" role="navigation" aria-label="Modulos disponibles">
            {vistas.map((v) => (
              <button
                key={v.id}
                className={`gv-tab${vista.id === v.id ? ' active' : ''}`}
                onClick={() => setVistaActiva(v.id)}
              >
                {v.label}
              </button>
            ))}
          </nav>

          <main className={`gv-main gv-fade-in ${vista?.id === 'admin' ? 'gv-main--admin' : ''}`}>
            <div className="module-heading">
              <span>Módulo actual</span>
              <h1>{moduleTitle}</h1>
            </div>
            {renderView(vista?.id)}
          </main>
        </>
      )}

      <footer className="gv-footer">
        (c) {new Date().getFullYear()} GasValpo S.A. - GasValpo OT Cobranza
      </footer>
    </div>
  );
}

export default App;
